import React, { useState, useRef } from 'react';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiUrlsWithFallback } from '../../../config/api';
import { Button } from '@/Components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/Components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/Components/ui/feedback';
import { Progress } from '@/Components/ui/feedback';
import { Heading, Text } from '@/Components/ui/typography';
import { Upload, FileText, CheckCircle2, Loader2, Lightbulb, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';

function UploadJobDescription({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx', '.txt'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        setError('Please upload a valid file (PDF, DOC, DOCX, or TXT)');
        setSelectedFile(null);
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size should not exceed 10MB');
        setSelectedFile(null);
        return;
      }
      
      setError('');
      setSelectedFile(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const event = { target: { files: [file] } };
      handleFileChange(event);
    }
  };

  // Handle file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    
    // Validate file selection
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    
    // Prevent multiple clicks
    if (isUploading) {
      return;
    }
    
    setIsUploading(true);
    setError('');
    setUploadProgress(10);
    
    try {
      // Try both localhost and production URLs
      const urls = getApiUrlsWithFallback('/api/upload_jd');
      
      let response;
      let lastError;
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      // Company context is REQUIRED by the backend (company_id). In the Company
      // Admin panel the target company arrives as ?companyID=; on the employer
      // side it lives in localStorage. Without it /api/upload_jd returns 400.
      const companyIDParam = new URLSearchParams(location.search).get('companyID');
      formData.append('company_id', companyIDParam || localStorage.getItem('companyAccountId') || '');
      formData.append('company_name', localStorage.getItem('company_name') || '');
      formData.append('company_email', localStorage.getItem('employer_email') || '');

      setUploadProgress(30);
      
      for (const url of urls) {
        try {
          // Debug log removed — was leaking API URL to console.
          response = await fetch(url, {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            // Debug log removed — was leaking API URL to console.
            break;
          }
        } catch (err) {
          // Debug log removed — was leaking API URL to console.
          lastError = err;
          continue;
        }
      }

      if (!response) {
        throw lastError || new Error('Failed to connect to API servers');
      }

      setUploadProgress(60);

      // Debug logs removed — were leaking response status/headers to console.

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`Failed to generate job description: ${response.status}`);
      }

      const data = await response.json();
      // Debug log removed — was leaking parsed JD response to console.

      setUploadProgress(80);

      const generatedJdData = data.job_details;
      // Debug log removed — was leaking generated JD payload to console.

      // Store the generated data in localStorage and navigate to JD creation page
      localStorage.setItem('generatedJdData', JSON.stringify(generatedJdData));
      // Debug log removed — was state-machine noise.

      setUploadProgress(100);
      setShowSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Navigate after short delay to show success
      setTimeout(() => {
        const companyID = new URLSearchParams(location.search).get('companyID');
        // Decide target based on context: if embedded (Company Admin) or companyID present -> admin route; else -> HireWithUs route
        const target = (companyID)
          ? (companyID ? `/create-JD?companyID=${encodeURIComponent(companyID)}` : '/create-JD')
          : '/hirewithus/postjob';
        // Debug log removed — was state-machine noise.
        navigate(target);
      }, 2000);
    } catch (error) {
      console.error('JD Generation Error:', error);
      setError(error.message || 'Failed to upload job description. Please try again.');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const Inner = (
      <div className="flex flex-col w-full gap-6">
        {/* Back Navigation */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (embedded ? navigate(-1) : navigate('/hirewithus/postjob'))}
          className="w-fit"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {embedded ? 'Back' : 'Back to Post Job Options'}
        </Button>
        
        <div className="flex flex-col w-full gap-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-md bg-primary/10">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <Heading level="h4">Upload Job Description</Heading>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload Section */}
            <Card>
                        <CardHeader>
                          <CardTitle className="text-base font-semibold">Upload Your File</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Error Message */}
                          {error && (
                            <Alert variant="destructive">
                              <AlertTitle>Error</AlertTitle>
                              <AlertDescription>{error}</AlertDescription>
                            </Alert>
                          )}

                          {/* Success Message */}
                          {showSuccess && (
                            <Alert variant="success" className="flex items-start gap-3 p-4">
                              <CheckCircle2 className="h-5 w-5 mt-0.5 text-primary" />
                              <div>
                                <AlertTitle>Success</AlertTitle>
                                <AlertDescription>
                                  Job Description uploaded and processed successfully!
                                </AlertDescription>
                              </div>
                            </Alert>
                          )}

                          {/* Upload Area */}
                          <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={cn(
                              "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
                              isDragging
                                ? "border-primary bg-primary/5"
                                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50",
                              selectedFile && "border-primary bg-primary/5"
                            )}
                          >
                            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                            <Text size="lg" weight="semibold" className="mb-2">
                              Drag & drop files or click to browse
                            </Text>
                            <Text size="sm" variant="muted">
                              Supported formats: PDF, DOC, DOCX, TXT (Max 10MB)
                            </Text>
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="hidden"
                              onChange={handleFileChange}
                              accept=".pdf,.doc,.docx,.txt"
                              disabled={isUploading}
                            />
                          </div>

                          {/* Selected File Info */}
                          {selectedFile && !isUploading && (
                            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                              <FileText className="h-5 w-5 text-primary" />
                              <div className="flex-1 min-w-0">
                                <Text size="sm" weight="medium" className="truncate">
                                  {selectedFile.name}
                                </Text>
                                <Text size="xs" variant="muted">
                                  {(selectedFile.size / 1024).toFixed(2)} KB
                                </Text>
                              </div>
                            </div>
                          )}

                          {/* Upload Progress */}
                          {isUploading && (
                            <div className="space-y-2">
                              <Progress value={uploadProgress} />
                              <Text size="sm" variant="muted" className="text-center">
                                Uploading and processing... {uploadProgress}%
                              </Text>
                            </div>
                          )}
                        </CardContent>
                        <CardFooter>
                          <Button
                            variant="default"
                            onClick={handleUpload}
                            disabled={isUploading || !selectedFile}
                            className="w-full"
                            size="lg"
                            style={{
                              backgroundColor: getColor('primary'),
                              color: getColor('primaryForeground'),
                              borderColor: getColor('primary')
                            }}
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Upload className="mr-2 h-4 w-4" />
                                Upload JD
                              </>
                            )}
                          </Button>
                        </CardFooter>
                      </Card>

                        {/* Instructions Panel */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center gap-2.5">
                              <div className="p-1.5 rounded-md bg-primary/10">
                                <Lightbulb className="h-4 w-4 text-primary" />
                              </div>
                              <CardTitle className="text-base font-semibold">How It Works</CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-6">
                            {/* Step 1 */}
                            <div className="flex gap-4">
                              <div 
                                className="flex-shrink-0 w-10 h-10 rounded-full text-white flex items-center justify-center font-bold text-base shadow-md"
                                style={{
                                  backgroundColor: getColor('primary'),
                                  color: getColor('primaryForeground'),
                                }}
                              >
                                1
                              </div>
                              <div className="flex-1">
                                <Heading level="h3" className="text-lg font-semibold mb-1">
                                  Upload Your Document
                                </Heading>
                                <Text size="sm" variant="muted">
                                  Select a job description file in PDF, DOC, DOCX, or TXT format
                                </Text>
                              </div>
                            </div>

                            {/* Step 2 */}
                            <div className="flex gap-4">
                              <div 
                                className="flex-shrink-0 w-10 h-10 rounded-full text-white flex items-center justify-center font-bold text-base shadow-md"
                                style={{
                                  backgroundColor: getColor('primary'),
                                  color: getColor('primaryForeground'),
                                }}
                              >
                                2
                              </div>
                              <div className="flex-1">
                                <Heading level="h3" className="text-lg font-semibold mb-1">
                                  AI Processing
                                </Heading>
                                <Text size="sm" variant="muted">
                                  Our AI extracts and structures all relevant job details automatically
                                </Text>
                              </div>
                            </div>

                            {/* Step 3 */}
                            <div className="flex gap-4">
                              <div 
                                className="flex-shrink-0 w-10 h-10 rounded-full text-white flex items-center justify-center font-bold text-base shadow-md"
                                style={{
                                  backgroundColor: getColor('primary'),
                                  color: getColor('primaryForeground'),
                                }}
                              >
                                3
                              </div>
                              <div className="flex-1">
                                <Heading level="h3" className="text-lg font-semibold mb-1">
                                  Review & Post
                                </Heading>
                                <Text size="sm" variant="muted">
                                  Review the generated job posting and publish it to attract candidates
                                </Text>
                              </div>
                            </div>
                          </CardContent>
                          <CardFooter>
                            <Text size="sm" variant="muted" className="text-center w-full">
                              ✨ Our AI will transform your document into a structured job posting in seconds!
                            </Text>
                          </CardFooter>
                        </Card>
          </div>
        </div>
      </div>
  );

  if (embedded) {
    return Inner;
  }

  return <EmployerLayout>{Inner}</EmployerLayout>;
}

export default UploadJobDescription;